import React, { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../../app/store';
import { NoticiaList } from '../../../../../typesSite/noticia/Noticia';
import NoticiaPublicListFiltroForm from './NoticiaPublicListFiltroForm';
import configSiteUrls from '../../../configs/configSiteUrls';
import NoticiaPublicCardForm from './NoticiaPublicCardForm';
import { useGetNoticiasPublicQuery } from '../noticiaPublicApiSlice';
import errorFormat from '../../../../../utils/errorFormat';

export function NoticiaPublicList() {
  const [noticias, setNoticias] = useState<NoticiaList[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const loaderRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const filtro = useSelector((state: RootState) => state.noticiaPublicListFiltro);

  // RTK Query
  const { data, isFetching, isLoading, error } = useGetNoticiasPublicQuery({
    data_inicial: filtro.dataInicial,
    data_final: filtro.dataFinal,
    search: filtro.search,
    page,
    apply: filtro.apply
  });

  // Atualiza estado com os dados retornados
  useEffect(() => {
    if (!data) return;

    if (page === 1) {
      setNoticias(data.results);
    } else {
      setNoticias(prev => [...prev, ...data.results]);
    }

    setHasMore(Boolean(data.next));
    setInitialLoading(false);
  }, [data, page]);

  // Erro ao carregar
  useEffect(() => {
    if (error) {
      enqueueSnackbar('Erro ao carregar as notícias.', { variant: 'error' });
      const erroReturn: string = errorFormat(error);
      enqueueSnackbar(erroReturn, { variant: 'error' });
      console.log(error);
    }
  }, [error, enqueueSnackbar]);

  // Reset ao mudar filtros
  useEffect(() => {
    setNoticias([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [filtro.apply]);

  // Scroll infinito
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isFetching && !initialLoading) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '200px' }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isFetching, initialLoading]);

  // Restaura a página salva para carregar as notícias corretas
  useEffect(() => {
    const scrollData = localStorage.getItem('scrollPublicacao');
    if (!scrollData) return;

    const { page: savedPage } = JSON.parse(scrollData);
    if (savedPage) {
      setPage(savedPage);
    }
  }, []);

  // Scroll restore com limite de tentativas e cancelamento
  useEffect(() => {
    const scrollData = localStorage.getItem('scrollPublicacao');
    if (!scrollData) return;

    const { posicao, data, idnoticia } = JSON.parse(scrollData);
    const now = new Date();
    const savedTime = new Date(data);
    const timeDifference = now.getTime() - savedTime.getTime();

    if (timeDifference > 600000) {
      localStorage.removeItem('scrollPublicacao');
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;
    let isCancelled = false;

    const tryScrollToNoticia = () => {
      if (isCancelled) return;

      const el = document.getElementById(idnoticia);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        localStorage.removeItem('scrollPublicacao');
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScrollToNoticia, 300);
      } else {
        console.warn('Não foi possível encontrar o elemento da notícia para scroll.');
        localStorage.removeItem('scrollPublicacao');
      }
    };

    tryScrollToNoticia();

    return () => {
      isCancelled = true;
    };
  }, [noticias]);

  // Tela de carregamento inicial
  if (initialLoading && noticias.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <NoticiaPublicListFiltroForm />

      {noticias.length === 0 && !isFetching && (
        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
          Nenhum registro/notícia encontrada com os filtros aplicados.
        </Typography>
      )}

      {noticias.map(n => (
        <NoticiaPublicCardForm key={n.id} noticia={n} />
      ))}

      {isFetching && page > 1 && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      <div ref={loaderRef} />
    </Box>
  );
}
